import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Mail, Loader2, Trash2, AlertTriangle, ShieldCheck } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const inputClass =
  "w-full rounded-xl border border-slate-300 bg-white px-4 py-3 text-sm text-slate-900 placeholder:text-slate-400 outline-none transition-[border-color,box-shadow] duration-300 focus:border-[#024AD8] focus:ring-4 focus:ring-[#024AD8]/15";

export default function EmailSettings({ token }) {
  const [settings, setSettings] = useState(null);
  const [apiKey, setApiKey] = useState("");
  const [fromEmail, setFromEmail] = useState("hi@meetingsupgraded.com");
  const [fromName, setFromName] = useState("HP Poly Gen 2 Migration");
  const [testRecipient, setTestRecipient] = useState("");
  const [saving, setSaving] = useState(false);
  const [removing, setRemoving] = useState(false);

  const headers = { Authorization: `Bearer ${token}` };

  const load = async () => {
    try {
      const { data } = await axios.get(`${API}/admin/settings/email`, { headers });
      setSettings(data);
      setFromEmail(data.from_email);
      setFromName(data.from_name);
    } catch {
      toast.error("Could not load email settings.");
    }
  };

  useEffect(() => { load(); }, []);

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const { data } = await axios.put(
        `${API}/admin/settings/email`,
        { api_key: apiKey, from_email: fromEmail, from_name: fromName, test_recipient: testRecipient },
        { headers }
      );
      toast.success(`Resend verified — test email sent to ${testRecipient}.`);
      setApiKey("");
      setTestRecipient("");
      await load();
    } catch (e2) {
      toast.error(e2.response?.data?.detail || "Could not save email settings.");
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    setRemoving(true);
    try {
      await axios.delete(`${API}/admin/settings/email`, { headers });
      toast.success("Resend key removed — emails now use the platform sender.");
      await load();
    } catch {
      toast.error("Could not remove the Resend key.");
    } finally {
      setRemoving(false);
    }
  };

  return (
    <div data-testid="email-settings-card" className="max-w-2xl rounded-2xl border border-slate-200 bg-white p-8 shadow-sm">
      <div className="flex items-center gap-3">
        <span className="w-10 h-10 rounded-xl bg-[#EAF0FC] grid place-items-center">
          <Mail className="w-5 h-5 text-[#024AD8]" />
        </span>
        <div>
          <p className="font-display text-lg font-bold text-slate-900 leading-tight">Email delivery</p>
          <p className="text-xs text-slate-500">Choose how campaign emails are sent</p>
        </div>
      </div>

      <div data-testid="email-mode-status" className={`mt-6 rounded-xl border px-4 py-3.5 text-sm font-medium ${
        settings?.configured
          ? "border-emerald-200 bg-emerald-50 text-emerald-800"
          : "border-slate-200 bg-slate-50 text-slate-600"
      }`}>
        {!settings ? "Loading…" : settings.configured ? (
          <>Sending directly from <strong>{settings.from_email}</strong> via your Resend account ({settings.api_key_preview}).</>
        ) : (
          <>Using the platform sender — you&apos;re all set, no save needed. Add a Resend API key below only if you want to send from your own address.</>
        )}
      </div>

      {settings?.last_error && (
        <div data-testid="email-last-error" className="mt-4 flex items-start gap-2.5 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3.5 text-sm text-amber-800">
          <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
          <span>Last send failed: {settings.last_error}. Update the key or check your Resend domain verification.</span>
        </div>
      )}

      <form onSubmit={save} className="mt-6 space-y-5">
        <div>
          <label htmlFor="resend-api-key" className="block text-xs font-semibold text-slate-700 mb-2 uppercase tracking-wide">Resend API key *</label>
          <input
            id="resend-api-key"
            data-testid="resend-api-key-input"
            type="password"
            required
            value={apiKey}
            onChange={(e) => setApiKey(e.target.value)}
            placeholder="re_xxxxxxxxxxxx"
            className={inputClass}
          />
          <p className="mt-1.5 text-xs text-slate-400">Stored encrypted, never shown again — only a masked preview is kept.</p>
        </div>
        <div className="grid sm:grid-cols-2 gap-5">
          <div>
            <label htmlFor="resend-from-email" className="block text-xs font-semibold text-slate-700 mb-2 uppercase tracking-wide">From email *</label>
            <input
              id="resend-from-email"
              data-testid="resend-from-email-input"
              type="email"
              required
              value={fromEmail}
              onChange={(e) => setFromEmail(e.target.value)}
              placeholder="hi@meetingsupgraded.com"
              className={inputClass}
            />
          </div>
          <div>
            <label htmlFor="resend-from-name" className="block text-xs font-semibold text-slate-700 mb-2 uppercase tracking-wide">From name *</label>
            <input
              id="resend-from-name"
              data-testid="resend-from-name-input"
              type="text"
              required
              value={fromName}
              onChange={(e) => setFromName(e.target.value)}
              placeholder="HP Poly Gen 2 Migration"
              className={inputClass}
            />
          </div>
        </div>
        <div>
          <label htmlFor="resend-test-recipient" className="block text-xs font-semibold text-slate-700 mb-2 uppercase tracking-wide">Send validation test to *</label>
          <input
            id="resend-test-recipient"
            data-testid="resend-test-recipient-input"
            type="email"
            required
            value={testRecipient}
            onChange={(e) => setTestRecipient(e.target.value)}
            placeholder="you@company.com"
            className={inputClass}
          />
          <p className="mt-1.5 text-xs text-slate-400">The key is only saved after this test email sends successfully — proving the domain is verified in Resend.</p>
        </div>
        <div className="flex items-center gap-3 pt-1">
          <button
            data-testid="resend-save-button"
            type="submit"
            disabled={saving}
            className="inline-flex items-center gap-2 bg-[#024AD8] hover:bg-[#0238A8] disabled:opacity-60 text-white text-sm font-semibold px-6 py-3 rounded-xl transition-colors duration-300"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <ShieldCheck className="w-4 h-4" />}
            {saving ? "Validating…" : "Validate & save"}
          </button>
          {settings?.configured && (
            <button
              data-testid="resend-remove-button"
              type="button"
              onClick={remove}
              disabled={removing}
              className="inline-flex items-center gap-2 text-sm font-semibold text-slate-600 border border-slate-300 rounded-xl px-5 py-3 hover:border-red-300 hover:text-red-600 transition-colors duration-300"
            >
              {removing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
              Remove key
            </button>
          )}
        </div>
      </form>
    </div>
  );
}
